import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HandHeart, Loader2 } from "lucide-react";
import { actionHelp } from "@/lib/api";
import { toast } from "sonner";
import { sampleBusiness } from "@/data/brief";

export default function HelpFlow({ open, onOpenChange, recommendation, onSubmitted }) {
  const [form, setForm] = useState({
    businessName: sampleBusiness.name,
    contactName: sampleBusiness.ownerFirstName,
    email: "sam@ridgelineplumbing.com",
    phone: "(208) 555-0142",
    additionalNotes: "",
  });
  const [busy, setBusy] = useState(false);

  if (!recommendation) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const valid =
    form.businessName.trim() &&
    form.contactName.trim() &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.trim().length >= 7;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await actionHelp(recommendation.id, form);
      toast.success("We've got it.", { description: "One of our advisors will reach out today." });
      onSubmitted?.();
      onOpenChange(false);
      setForm({ ...form, additionalNotes: "" });
    } catch (err) {
      toast.error("Couldn't send the request.", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 rounded-[24px] overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="help-flow-modal"
      >
        <DialogHeader className="px-6 pt-6">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
              style={{ background: "#FCE6DE", color: "var(--ta-secondary)" }}
            >
              <HandHeart size={22} strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <DialogTitle className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
                We'll handle this for you.
              </DialogTitle>
              <DialogDescription className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
                Quick check that we've got the right details. No payment right now.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pt-4 pb-6 flex flex-col gap-4">
          <TextField label="Business name" value={form.businessName} onChange={set("businessName")} testId="help-business" />
          <TextField label="Your name" value={form.contactName} onChange={set("contactName")} testId="help-name" />
          <TextField label="Email" value={form.email} onChange={set("email")} type="email" testId="help-email" />
          <TextField label="Phone" value={form.phone} onChange={set("phone")} type="tel" testId="help-phone" />

          <div>
            <label className="text-sm font-medium mb-2 inline-block" style={{ color: "var(--ta-primary)" }}>
              Anything we should know? <span style={{ color: "var(--ta-text-secondary)" }}>(optional)</span>
            </label>
            <textarea
              value={form.additionalNotes}
              onChange={set("additionalNotes")}
              data-testid="help-notes"
              rows={3}
              placeholder="Best time to reach you, preferred contact method, etc."
              className="w-full rounded-2xl bg-white border-2 p-4 text-base outline-none focus:border-[var(--ta-primary)] resize-none"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-text)" }}
            />
          </div>

          <div
            className="rounded-xl p-3 text-sm"
            style={{ background: "var(--ta-surface-alt)", color: "var(--ta-text-secondary)" }}
          >
            About: <span className="font-medium" style={{ color: "var(--ta-primary)" }}>{recommendation.title}</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              onClick={() => onOpenChange(false)}
              data-testid="help-cancel"
              className="flex-1 min-h-[56px] rounded-2xl text-base font-medium border-2"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
            >
              Never mind
            </button>
            <button
              onClick={submit}
              disabled={!valid || busy}
              data-testid="help-submit"
              className="flex-1 min-h-[56px] rounded-2xl text-base font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "var(--ta-secondary)" }}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2 justify-center"><Loader2 size={18} className="animate-spin" /> Sending…</span>
              ) : (
                "Send request"
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TextField = ({ label, value, onChange, testId, type = "text" }) => (
  <label className="block">
    <span className="text-sm font-medium mb-2 inline-block" style={{ color: "var(--ta-primary)" }}>
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full rounded-2xl bg-white border-2 px-4 py-3.5 text-base outline-none focus:border-[var(--ta-primary)]"
      style={{ borderColor: "var(--ta-border)", color: "var(--ta-text)" }}
    />
  </label>
);
